import { useMemo } from "react";
import {
  useListPatientDispositionsQuery,
  useListEDDQuery,
  useListPatientMilestonesQuery,
  useListIntelligenceTextQuery,
  useListMedicallyReadyStatusQuery,
  useListPatientInsightsQuery,
  useListPatientNotesQuery,
} from "../api/queries";
import PatientListEntry from "../types/PatientListEntry";
import { TypeChoices } from "../types/TypeChoices";

interface UsePatientListDetailsResult {
  data: PatientListEntry[];
  isLoading: boolean;
}

export const usePatientListDetails = (
  patientListData: PatientListEntry[]
): UsePatientListDetailsResult => {
  const patientIDs = useMemo(
    () => patientListData.map((p) => p.id),
    [patientListData]
  );

  // get all the details
  const patientDispositionsQuery = useListPatientDispositionsQuery(patientIDs);
  const eddQuery = useListEDDQuery(patientIDs);
  const patientMilestonesQuery = useListPatientMilestonesQuery(patientIDs);
  const intelligenceTextQuery = useListIntelligenceTextQuery(patientIDs);
  const medicallyReadyStatusQuery =
    useListMedicallyReadyStatusQuery(patientIDs);
  const patientInsightsQuery = useListPatientInsightsQuery(patientIDs);
  const patientNotesQuery = useListPatientNotesQuery(patientIDs);

  const allQueries = [
    patientDispositionsQuery,
    eddQuery,
    intelligenceTextQuery,
    medicallyReadyStatusQuery,
    patientMilestonesQuery,
    patientInsightsQuery,
    patientNotesQuery,
  ];

  const isLoading = allQueries.some((query) => query.isLoading);

  // Memoize the aggregated result
  const aggregatedData = useMemo(() => {
    // Get data even if some queries are still loading - use empty defaults
    const dispositions = patientDispositionsQuery.data ?? {};
    const edds = eddQuery.data ?? {};
    const milestones = patientMilestonesQuery.data ?? {};
    const intelText = intelligenceTextQuery.data ?? {};
    const medicallyReady = medicallyReadyStatusQuery.data ?? {};
    const insights = patientInsightsQuery.data ?? {};
    const notes = patientNotesQuery.data ?? {};

    // loop through each patient record and set the values
    const patientRecords = patientListData.map((p) => {
      const patientEdds = edds[p.id] ?? [];
      const patientDispositions = dispositions[p.id] ?? [];

      const currentEDD =
        patientEdds.find((e) => e.edd_type === TypeChoices.USER_ENTERED) ??
        null;

      const suggestedEDD =
        patientEdds.find(
          (e) => e.edd_type === TypeChoices.SMART_SUGGESTION && !e.status
        ) ?? null;

      const currentDispositions = patientDispositions.filter(
        (d) => d.disposition_type === TypeChoices.USER_ENTERED
      );

      const suggestedDisposition =
        patientDispositions.find(
          (d) =>
            d.disposition_type === TypeChoices.SMART_SUGGESTION && !d.status
        ) ?? null;

      return {
        ...p,
        current_edd: currentEDD,
        suggested_edd: suggestedEDD,
        current_dispositions: currentDispositions,
        suggested_disposition: suggestedDisposition,
        intelligence_text: intelText[p.id] ?? [],
        milestones: milestones[p.id] ?? [],
        medically_ready_status: medicallyReady[p.id] ?? null,
        insights: insights[p.id] ?? null,
        notes: notes[p.id] ?? [],
      };
    });

    return patientRecords;
  }, [
    patientListData,
    patientDispositionsQuery.data,
    eddQuery.data,
    intelligenceTextQuery.data,
    patientMilestonesQuery.data,
    medicallyReadyStatusQuery.data,
    patientInsightsQuery.data,
    patientNotesQuery.data,
  ]);

  return { data: aggregatedData, isLoading };
};
